// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@/components/theme-provider";
import { ResizableCard } from "@/components/ui/resizable-card";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import {
  type Activity,
  useAccountActivityTimeline,
} from "@/hooks/api/use-account-activities";
import { useAccountFirstTouchpointTimeline } from "@/hooks/api/use-account-first-touchpoint-timeline";
import { useAccountOpportunities } from "@/hooks/api/use-account-opportunities";
import { useAccountPersons } from "@/hooks/api/use-account-persons";
import { useMilestoneAnalyses } from "@/hooks/api/use-milestone-analyses";
import { useMilestoneAnalysisAdminEnabled } from "@/hooks/auth/use-permissions-gates";
import { useOrganizationSessionStorage } from "@/hooks/use-browser-storage";
import { useBuyingGroupSelection } from "@/hooks/use-buying-group-selection";
import { ChartHeaders } from "./chart-headers.tsx";
import { ActivityTooltip } from "./chart-tooltips.tsx";
import FilteredActivityDots from "./filtered-activity-dots.tsx";
import { FirstTouchpointsChart } from "./first-touchpoints-chart.tsx";
import { formatMilestoneAnalyses } from "./formatters.ts";
import { useChartData } from "./hooks.ts";
import { MilestoneAnalysesChart } from "./milestone-analyses-chart";
import { OpportunityChart } from "./opportunity-chart.tsx";
import { getZeroValuePeriodHeight } from "./utils.ts";

/**
 * This threshold determines when to show filtered activity dots.
 * If the number of filtered activities is less than 90% of total activities,
 * we show the dots to give users visual indication of filtered data.
 * The 90% threshold was chosen to avoid showing dots when only a few activities
 * are filtered out, which would add visual noise with little information value.
 */
const FILTERED_ACTIVITIES_THRESHOLD_RATIO = 0.9;

// Define what the MilestoneAnalyses prop expects in ActivityChartsProps
export interface MilestoneAnalysisChartData {
  id: string;
  opportunity_id: string;
  timestamp: number;
  moment_type: string;
}

interface ActivityChartsProps {
  accountId?: string;
  onDateClick?: (date: string) => void;
  onPersonClick?: (personId: string) => void;
  visibleRange?: { start: string; end: string };
  filteredActivities?: Activity[];
  selectedOpportunityIds?: string[];
  selectedPersonIds?: string[];
  customTimeRange: { start: Date; end: Date } | null;
  setCustomTimeRange: (range: { start: Date; end: Date } | null) => void;
  // Custom buying group opportunity IDs for minimap bars
  customGroupOpportunityIds?: string[];
}

interface ChartMouseEvent {
  activeLabel?: string | number;
  activePayload?: Array<{
    payload?: {
      id?: string;
      timestamp?: number;
    };
  }>;
  chartX?: number;
  chartY?: number;
}

export function ActivityCharts({
  accountId,
  onDateClick,
  onPersonClick,
  visibleRange,
  filteredActivities,
  selectedOpportunityIds,
  selectedPersonIds,
  customTimeRange,
  setCustomTimeRange,
  customGroupOpportunityIds,
}: ActivityChartsProps) {
  const { effectiveTheme } = useTheme();
  const { state: sidebarState, isMobile } = useSidebar();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null);
  const [showOutgoingLine, setShowOutgoingLine] = useState(false);

  // Store and retrieve chart height from organization-scoped sessionStorage
  const [chartHeight, setChartHeight] = useOrganizationSessionStorage<number>(
    "accounts::explorer::minimap::height",
    250,
  );

  // Define fixed elements and proportions
  const HANDLE_HEIGHT = 8; // Resize handle height (keep in sync with ResizableCard)
  const MIN_HEADER_HEIGHT = 30; // Minimum header height
  const MAX_HEADER_HEIGHT = 50; // Maximum header height
  const CHART_GAPS = 8; // Gaps between charts (2px * 4 charts)

  const isMilestoneAnalysisAdminEnabled = useMilestoneAnalysisAdminEnabled();

  // Calculate header height proportionally to total height (between min and max)
  const headerHeight = useMemo(() => {
    // Header takes between 10-15% of total height, with min/max constraints
    const targetHeight = Math.floor(chartHeight * 0.12);
    return Math.max(
      MIN_HEADER_HEIGHT,
      Math.min(MAX_HEADER_HEIGHT, targetHeight),
    );
  }, [chartHeight]);

  // Calculate the actual chart area height (excluding fixed elements)
  const chartAreaHeight = useMemo(
    () => chartHeight - HANDLE_HEIGHT - headerHeight - CHART_GAPS,
    [chartHeight, headerHeight],
  );

  // Calculate opportunity chart dynamic height based on the number of opportunities
  // Get the buying group selection context to determine the mode
  const { buyingGroupMode } = useBuyingGroupSelection();

  // Get opportunities data for dynamic height calculation
  // IMPORTANT: This implements a dual-filtering approach for custom buying groups:
  // 1. For touchpoint data, we use personIds (selectedPersonIds) to show all related touchpoints
  // 2. For opportunity bars in the minimap, we use customGroupOpportunityIds to only show
  //    the opportunities explicitly selected by the user in their custom buying group
  //
  // This decoupling gives users precise control over which opportunities appear in visualizations
  // while maintaining context by showing all relevant touchpoints.
  const { data: opportunities, isLoading: isLoadingOpportunities } =
    useAccountOpportunities(
      accountId,
      selectedOpportunityIds?.length ? selectedOpportunityIds : undefined,
      selectedPersonIds?.length ? selectedPersonIds : undefined,
      buyingGroupMode,
      // Pass custom group opportunity IDs when in custom mode to show explicitly selected opportunities
      buyingGroupMode === "custom" ? customGroupOpportunityIds : undefined,
    );

  const dynamicOpportunityHeight = useMemo(() => {
    if (!opportunities?.length) return 10; // Minimum height when no opportunities

    // Get unique opportunity IDs from the data
    const uniqueOpportunityIds = new Set(opportunities.map((o) => o.id));

    const count = uniqueOpportunityIds.size;
    const heightPerOpportunity = 6;

    // Calculate height with a minimum and maximum value
    return Math.max(20, Math.min(count * heightPerOpportunity, 100));
  }, [opportunities]);

  // Calculate milestone analysis height - give it a fixed proportion
  const milestoneAnalysisHeight = 20;

  // Calculate first touchpoints height based on remaining space
  const firstTouchpointsMinHeight = 40; // Minimum height for first touchpoints
  const firstTouchpointsOptimalHeight = 100; // Optimal height where dots don't need scaling
  const firstTouchpointsMaxHeight = 100; // Maximum height for first touchpoints - limit growth

  // Calculate proportions dynamically based on content
  const proportions = useMemo(() => {
    // Start with base values for the chart area
    const opportunityProportion = dynamicOpportunityHeight / chartAreaHeight;
    const milestoneAnalysisProportion =
      milestoneAnalysisHeight / chartAreaHeight;

    // First calculate what a third of the space would be (our target allocation)
    const targetProportion =
      Math.max(0, 1 - opportunityProportion - milestoneAnalysisProportion) *
      0.33;

    // Then determine what proportion would give us our optimal height
    const optimalProportion = firstTouchpointsOptimalHeight / chartAreaHeight;
    const maxProportion = firstTouchpointsMaxHeight / chartAreaHeight;

    // Use fixed height (as a proportion):
    // 1. Cap at maximum height (firstTouchpointsMaxHeight)
    // 2. Ensure we're at least at minimum height (firstTouchpointsMinHeight)
    // 3. Target is 1/3 of remaining space but don't grow beyond optimal size
    const firstTouchpointsProportion = Math.min(
      maxProportion, // Hard cap at maximum height
      Math.max(
        firstTouchpointsMinHeight / chartAreaHeight, // Ensure minimum height
        Math.min(
          optimalProportion, // Don't grow beyond optimal height
          targetProportion, // Target is 1/3 of remaining space
        ),
      ),
    );

    // Activity gets whatever is left after the other charts
    const activityProportion = Math.max(
      0,
      1 -
        opportunityProportion -
        milestoneAnalysisProportion -
        firstTouchpointsProportion,
    );

    return {
      activity: activityProportion,
      opportunity: opportunityProportion,
      milestoneAnalysis: milestoneAnalysisProportion,
      firstTouchpoints: firstTouchpointsProportion,
    };
  }, [chartAreaHeight, dynamicOpportunityHeight]);

  // Calculate individual chart heights, ensuring they sum exactly to chartAreaHeight
  const calculateHeights = useMemo(() => {
    // First calculate raw heights based on proportions (without rounding)
    const rawHeights = {
      activity: chartAreaHeight * proportions.activity,
      opportunity: chartAreaHeight * proportions.opportunity,
      milestoneAnalysis: chartAreaHeight * proportions.milestoneAnalysis,
      firstTouchpoints: chartAreaHeight * proportions.firstTouchpoints,
    };

    // Get integer heights, but tracking the fractional remainder
    const roundedHeights = {
      activity: Math.floor(rawHeights.activity),
      opportunity: Math.floor(rawHeights.opportunity),
      milestoneAnalysis: Math.floor(rawHeights.milestoneAnalysis),
      firstTouchpoints: Math.floor(rawHeights.firstTouchpoints),
    };

    // Calculate the total rounded height
    const totalRounded = Object.values(roundedHeights).reduce(
      (sum, h) => sum + h,
      0,
    );

    // Calculate remaining pixels to distribute (due to rounding)
    const remaining = chartAreaHeight - totalRounded;

    // Distribute remaining pixels based on the fractional parts
    // This ensures we distribute pixels to the charts with the largest fractional parts first
    if (remaining > 0) {
      const fractions = {
        activity: rawHeights.activity - roundedHeights.activity,
        opportunity: rawHeights.opportunity - roundedHeights.opportunity,
        milestoneAnalysis:
          rawHeights.milestoneAnalysis - roundedHeights.milestoneAnalysis,
        firstTouchpoints:
          rawHeights.firstTouchpoints - roundedHeights.firstTouchpoints,
      };

      // Sort chart names by their fractional parts in descending order
      const chartsSorted = Object.keys(fractions).sort(
        (a, b) =>
          fractions[b as keyof typeof fractions] -
          fractions[a as keyof typeof fractions],
      );

      // Distribute remaining pixels
      let pixelsLeft = remaining;
      for (const chart of chartsSorted) {
        if (pixelsLeft <= 0) break;
        roundedHeights[chart as keyof typeof roundedHeights]++;
        pixelsLeft--;
      }
    }

    return roundedHeights;
  }, [
    chartAreaHeight,
    proportions.activity,
    proportions.opportunity,
    proportions.milestoneAnalysis,
    proportions.firstTouchpoints,
  ]);

  // Extract individual heights from the calculated values
  const activityChartHeight = calculateHeights.activity;
  const opportunityChartHeight = calculateHeights.opportunity;
  const milestoneAnalysisChartHeight = calculateHeights.milestoneAnalysis;
  const firstTouchpointsChartHeight = calculateHeights.firstTouchpoints;

  // Using buying group mode declared earlier in the file
  const { data: timelineData, isLoading: isLoadingTimeline } =
    useAccountActivityTimeline(
      accountId,
      selectedOpportunityIds?.length ? selectedOpportunityIds : undefined,
      selectedPersonIds?.length ? selectedPersonIds : undefined,
      buyingGroupMode, // Pass the buying group mode
    );
  const { data: firstTouchpointData, isLoading: isLoadingFirstTouchpoints } =
    useAccountFirstTouchpointTimeline(
      accountId,
      selectedOpportunityIds?.length ? selectedOpportunityIds : undefined,
      selectedPersonIds?.length ? selectedPersonIds : undefined,
      buyingGroupMode, // Pass the buying group mode
      buyingGroupMode === "custom" ? customGroupOpportunityIds : undefined,
    );
  const { data: taskGroups } = useMilestoneAnalyses(
    accountId,
    buyingGroupMode === "custom"
      ? customGroupOpportunityIds
      : selectedOpportunityIds?.length
        ? selectedOpportunityIds
        : undefined,
    buyingGroupMode,
    true,
  );
  const { getPersonById } = useAccountPersons(accountId);

  // Use the custom hook to get all chart data
  const {
    visibleTimeRange,
    chartWidth,
    activityChartData,
    zeroValuePeriods,
    domain,
    ticks,
    tickFormatter,
  } = useChartData(
    timelineData,
    opportunities,
    firstTouchpointData,
    customTimeRange,
    getPersonById,
  );

  // Get the active analysis from each task group for chart display
  const activeAnalyses = useMemo(() => {
    if (!taskGroups) return [];

    const analyses =
      taskGroups
        ?.map(
          (group) =>
            group.analyses.find((a) => a.is_active_version) ||
            group.analyses[0],
        )
        .filter(Boolean) || [];

    // When milestone analysis flag is disabled, only use active analyses
    // When enabled, show all analyses
    return isMilestoneAnalysisAdminEnabled
      ? analyses
      : analyses.filter((a) => a.is_active_version);
  }, [taskGroups, isMilestoneAnalysisAdminEnabled]);

  // Format milestone analysis for display on the chart
  const MilestoneAnalyses = useMemo(
    () => formatMilestoneAnalyses(activeAnalyses, visibleTimeRange),
    [activeAnalyses, visibleTimeRange],
  );

  // Process milestone analysis for the chart if provided
  const chartDataWithMoments = MilestoneAnalyses?.length
    ? activityChartData.map((dataPoint) => {
        // Find any milestone analysis that match this timestamp (to within a day)
        const matchingMoments = MilestoneAnalyses.filter(
          (moment) =>
            Math.abs(moment.timestamp - dataPoint.timestamp) <
            24 * 60 * 60 * 1000,
        );

        return {
          ...dataPoint,
          MilestoneAnalyses: matchingMoments.length
            ? matchingMoments
            : undefined,
        };
      })
    : activityChartData;

  // Add drag selection handlers
  const handleMouseDown = (e: ChartMouseEvent) => {
    if (!e?.activeLabel) return;
    setIsDragging(true);
    setSelectionStart(
      typeof e.activeLabel === "string"
        ? parseInt(e.activeLabel, 10)
        : e.activeLabel,
    );
    setSelectionEnd(
      typeof e.activeLabel === "string"
        ? parseInt(e.activeLabel, 10)
        : e.activeLabel,
    );
  };

  const handleMouseMove = (e: ChartMouseEvent) => {
    if (!isDragging || !e?.activeLabel) return;
    setSelectionEnd(
      typeof e.activeLabel === "string"
        ? parseInt(e.activeLabel, 10)
        : e.activeLabel,
    );
  };

  const handleMouseUp = () => {
    if (!isDragging || !selectionStart || !selectionEnd) {
      // If we weren't dragging, this was a click
      if (!isDragging && selectionStart) {
        onDateClick?.(new Date(selectionStart).toISOString());
      }
      setIsDragging(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }

    // Ensure start is before end
    const start = Math.min(selectionStart, selectionEnd);
    const end = Math.max(selectionStart, selectionEnd);

    // If selection is too small (less than 1 week), treat as a click
    if (end - start < 7 * 24 * 60 * 60 * 1000) {
      onDateClick?.(new Date(start).toISOString());
      setIsDragging(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }

    // Set custom time range
    setCustomTimeRange({
      start: new Date(start),
      end: new Date(end),
    });

    // Reset selection state
    setIsDragging(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    // Set tab to the start date
    onDateClick?.(new Date(start).toISOString());
  };

  // Add effect to handle scrolling when custom range changes
  useEffect(() => {
    if (
      !scrollContainerRef.current ||
      !visibleTimeRange?.selectedRange ||
      !domain
    )
      return;

    const container = scrollContainerRef.current;
    const { selectedRange } = visibleTimeRange;
    const [domainStart, domainEnd] = domain;
    const domainWidth = domainEnd - domainStart;

    // Calculate the scroll position based on the selected range start
    const scrollPercentage =
      (selectedRange.start.getTime() - domainStart) / domainWidth;
    const scrollPosition = container.scrollWidth * scrollPercentage;

    // Scroll to position
    container.scrollLeft = scrollPosition;
  }, [visibleTimeRange?.selectedRange, domain, visibleTimeRange]);

  // Calculate max width based on viewport and sidebar state
  const maxWidth = isMobile
    ? "calc(100vw - 2rem)"
    : sidebarState === "collapsed"
      ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
      : "calc(100vw - var(--sidebar-width) - 2rem)";

  if (isLoadingTimeline) {
    return (
      <div className="relative w-full" style={{ maxWidth }}>
        <Skeleton className="w-full" style={{ height: `${chartHeight}px` }} />
      </div>
    );
  }

  return (
    <div className="relative w-full" style={{ maxWidth }}>
      <ResizableCard
        defaultHeight={chartHeight}
        minHeight={150}
        maxHeight={700}
        onHeightChange={setChartHeight}
        roundedBottom={false}
        className="overflow-visible"
      >
        <ChartHeaders
          showOutgoingLine={showOutgoingLine}
          setShowOutgoingLine={setShowOutgoingLine}
          customTimeRange={customTimeRange}
          setCustomTimeRange={setCustomTimeRange}
          headerHeight={headerHeight}
        />

        <div
          ref={scrollContainerRef}
          className={`relative w-full px-6 ${
            customTimeRange ? "overflow-x-auto" : ""
          }`}
        >
          <div style={{ width: chartWidth }}>
            <div
              className="relative overflow-visible"
              style={{ height: `${activityChartHeight}px` }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartDataWithMoments}
                  margin={{ top: 0, right: 20, bottom: -30, left: 25 }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <XAxis
                    dataKey="timestamp"
                    scale="time"
                    type="number"
                    domain={domain}
                    ticks={ticks}
                    interval={0}
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                    height={30}
                  />
                  <YAxis hide domain={[-2, 10]} />
                  <Tooltip
                    content={<ActivityTooltip />}
                    wrapperStyle={{ position: "absolute", zIndex: 50 }}
                    cursor={{ stroke: "hsl(var(--chart-5))", strokeWidth: 1 }}
                    position={{ y: 0 }}
                    isAnimationActive={false}
                  />

                  {/* X-axis reference line that moves with the data - rendered first so it's behind the lines */}
                  <ReferenceLine
                    y={0}
                    stroke="hsl(var(--border))"
                    strokeWidth={1}
                    pointerEvents="none"
                  />

                  <Line
                    type="monotone"
                    dataKey="decayed_in_value"
                    stroke="hsl(var(--chart-5))"
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                  {showOutgoingLine && (
                    <Line
                      type="monotone"
                      dataKey="decayed_out_value"
                      stroke="hsl(var(--muted-foreground) / 0.5)"
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                      name="decayed_out_value"
                    />
                  )}

                  {zeroValuePeriods.map((period, index) => {
                    const height = getZeroValuePeriodHeight(
                      period,
                      customTimeRange,
                    );
                    return (
                      <ReferenceArea
                        key={`zero-period-${index}`}
                        x1={period.start}
                        x2={period.end}
                        y1={-height}
                        y2={height}
                        fill={
                          effectiveTheme === "dark"
                            ? "hsl(var(--accent))"
                            : "hsl(var(--accent))"
                        }
                        fillOpacity={1}
                        stroke="transparent"
                        pointerEvents="none"
                      />
                    );
                  })}
                  {visibleRange && (
                    <>
                      <ReferenceArea
                        x1={Math.max(
                          domain[0],
                          new Date(visibleRange.start).getTime(),
                        )}
                        x2={Math.min(
                          domain[1],
                          new Date(visibleRange.end).getTime(),
                        )}
                        fill="transparent"
                        stroke="transparent"
                        label={({ viewBox }) => {
                          const { x, y, width } = viewBox;
                          return (
                            <line
                              x1={x}
                              y1={y}
                              x2={x + width}
                              y2={y}
                              stroke="hsl(var(--chart-5))"
                              strokeWidth={1}
                              pointerEvents="none"
                            />
                          );
                        }}
                        className="transition-all duration-200 ease-out"
                        pointerEvents="none"
                      />
                      <ReferenceLine
                        x={Math.max(
                          domain[0],
                          new Date(visibleRange.start).getTime(),
                        )}
                        stroke="hsl(var(--chart-5))"
                        strokeWidth={0.5}
                        className="transition-all duration-200 ease-out"
                        pointerEvents="none"
                      />
                      <ReferenceLine
                        x={Math.min(
                          domain[1],
                          new Date(visibleRange.end).getTime(),
                        )}
                        stroke="hsl(var(--chart-5))"
                        strokeWidth={0.5}
                        className="transition-all duration-200 ease-out"
                        pointerEvents="none"
                      />
                    </>
                  )}

                  {isDragging && selectionStart && selectionEnd && (
                    <ReferenceArea
                      x1={Math.min(selectionStart, selectionEnd)}
                      x2={Math.max(selectionStart, selectionEnd)}
                      fill="hsl(var(--chart-5))"
                      fillOpacity={0.2}
                      stroke="hsl(var(--chart-5))"
                      strokeOpacity={0.5}
                      pointerEvents="none"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>

              {filteredActivities &&
                timelineData?.activities &&
                filteredActivities.length > 0 &&
                filteredActivities.length <
                  timelineData.activities.length *
                    FILTERED_ACTIVITIES_THRESHOLD_RATIO && (
                  <div className="absolute left-[25px] right-[20px] bottom-0 h-4">
                    <FilteredActivityDots
                      data={filteredActivities}
                      domain={domain as [number, number]}
                      height={8}
                      onDateClick={onDateClick}
                    />
                  </div>
                )}
            </div>

            <div style={{ height: `${opportunityChartHeight}px` }}>
              <OpportunityChart
                accountId={accountId}
                domain={domain as [number, number]}
                ticks={ticks}
                visibleRange={visibleRange}
                visibleTimeRange={visibleTimeRange}
                isLoading={isLoadingOpportunities}
                onDateClick={onDateClick}
              />
            </div>

            <div style={{ height: `${milestoneAnalysisChartHeight}px` }}>
              <MilestoneAnalysesChart
                MilestoneAnalyses={MilestoneAnalyses}
                domain={domain as [number, number]}
                ticks={ticks}
                onDateClick={onDateClick}
                isLoading={isLoadingFirstTouchpoints}
                visibleRange={visibleRange}
                visibleTimeRange={visibleTimeRange}
              />
            </div>
            <div style={{ height: `${firstTouchpointsChartHeight}px` }}>
              <FirstTouchpointsChart
                accountId={accountId || ""}
                visibleRange={visibleRange}
                visibleTimeRange={visibleTimeRange}
                customTimeRange={customTimeRange}
                domain={domain as [number, number]}
                ticks={ticks}
                tickFormatter={tickFormatter}
                onPersonClick={onPersonClick}
                onDateClick={onDateClick}
                isLoading={isLoadingFirstTouchpoints}
              />
            </div>
          </div>
        </div>
      </ResizableCard>
    </div>
  );
}
